import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { chartDataSchema } from '../types'
import type { ChartData } from '../types'
import { useTranslation } from 'react-i18next'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ChartBlockProps {
  raw: string
  isStreaming?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PALETTE = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#a855f7']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getColor(index: number, overrideColor?: string): string {
  return overrideColor ?? PALETTE[index % PALETTE.length] ?? '#6366f1'
}

/** Transform chart data into recharts-friendly row format */
function buildRows(chart: ChartData): Record<string, string | number>[] {
  return chart.labels.map((label, rowIndex) => {
    const row: Record<string, string | number> = { name: label }
    for (const [dsIndex, ds] of chart.datasets.entries()) {
      const key = ds.label ?? `série_${String(dsIndex + 1)}`
      row[key] = ds.data[rowIndex] ?? 0
    }
    return row
  })
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ErrorBlock({ message }: Readonly<{ message: string }>) {
  return (
    <div className="my-3 rounded-xl border border-[var(--color-error)] bg-[var(--color-error-light)] px-4 py-3 text-sm text-[var(--color-error)]">
      {message}
    </div>
  )
}

function ChartTitle({ title }: Readonly<{ title?: string }>) {
  if (!title) return null
  return <p className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">{title}</p>
}

function CartesianChart({ chart }: Readonly<{ chart: ChartData }>) {
  const rows = buildRows(chart)
  const unit = chart.unit ?? ''
  const tooltipFormatter = (value: unknown): [string, string] => [
    `${String(value)}${unit}`,
    '',
  ]

  const seriesKeys = chart.datasets.map((ds, i) => ds.label ?? `série_${String(i + 1)}`)

  const sharedProps = {
    data: rows,
    margin: { top: 5, right: 10, left: 0, bottom: 5 },
  }

  if (chart.type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart {...sharedProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} unit={unit} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend />
          {seriesKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getColor(i, chart.datasets[i]?.color)}
              dot={false}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    )
  }

  if (chart.type === 'area') {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart {...sharedProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} unit={unit} />
          <Tooltip formatter={tooltipFormatter} />
          <Legend />
          {seriesKeys.map((key, i) => {
            const color = getColor(i, chart.datasets[i]?.color)
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                fill={color}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            )
          })}
        </AreaChart>
      </ResponsiveContainer>
    )
  }

  // default: bar
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart {...sharedProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} unit={unit} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        {seriesKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={getColor(i, chart.datasets[i]?.color)} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

function PieChartBlock({ chart }: Readonly<{ chart: ChartData }>) {
  const firstDataset = chart.datasets[0]
  if (!firstDataset) return null

  const unit = chart.unit ?? ''
  const tooltipFormatter = (value: unknown): [string, string] => [
    `${String(value)}${unit}`,
    '',
  ]
  const pieData = chart.labels.map((label, i) => ({
    name: label,
    value: firstDataset.data[i] ?? 0,
  }))

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          outerRadius={90}
          dataKey="value"
          label={(entry: { name?: string; value?: number }) =>
            `${entry.name ?? ''}: ${String(entry.value ?? 0)}${unit}`
          }
          labelLine={false}
        >
          {pieData.map((_entry, index) => (
            <Cell key={String(index)} fill={getColor(index)} />
          ))}
        </Pie>
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ChartBlock({ raw, isStreaming }: Readonly<ChartBlockProps>) {
  const { t } = useTranslation()
  if (isStreaming) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return <ErrorBlock message={t('common.chart_invalid')} />
  }

  const result = chartDataSchema.safeParse(parsed)
  if (!result.success) {
    return <ErrorBlock message={t('common.chart_unknown')} />
  }

  const chart = result.data

  return (
    <div className="my-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <ChartTitle title={chart.title} />
      {chart.type === 'pie' ? <PieChartBlock chart={chart} /> : <CartesianChart chart={chart} />}
    </div>
  )
}
